import * as React from "react";
import { useMinimumWageQuery } from "../useMinimumWageQuery";
import { MinimalWagePageProps } from "../types";
import { useMinimumWageMutation } from "../useMinimumWageMutation";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { MinimumWageCountryLayout } from "../components/MinimumWageCountryLayout";
import { FieldName, MinimumWageOptionsSection } from "../containers/MinimumWageOptionsSection";
import { MinimumWageAllowanceExpandableItemSection } from "../containers/MinimumWageAllowanceExpandableItemSection";
import { CalculationStartDateSection } from "../containers/CalculationStartDateSection";
import { MinimumWagePolishOptionsSection } from "../containers/MinimumWagePolishOptionsSection";
import { GenericMinimumWageExpandableItemSection } from "../containers/GenericMinimumWageExpandableItemSection";
import { DriverQualificationsType } from "@/app/constants/driverQualificationsType";
import { ItalyMinimumWageConfigurationModel } from "@/app/models/configuration/minimumWage/italyMinimumWageConfigurationModel";

import { PreLoader } from "../components/PreLoader"
import { buildMinimumWageDiffRenderers } from '../minimumWageDiffUtils';

const QUALIFICATION_TYPES_ITALY = [
    DriverQualificationsType.NotSet,
    DriverQualificationsType.VehiclesBelow8Group3,
    DriverQualificationsType.VehiclesAbove8Group3Super,
    DriverQualificationsType.CcnlC3,
    DriverQualificationsType.CcnlB3,
    DriverQualificationsType.CcnlA3,
    DriverQualificationsType.CcnlF2,
    DriverQualificationsType.CcnlE2,
    DriverQualificationsType.CcnlD2,
    DriverQualificationsType.CcnlH1,
    DriverQualificationsType.CcnlG1,
    DriverQualificationsType.CcnlI110,
    DriverQualificationsType.CcnlI116,
    DriverQualificationsType.CcnlL110,
    DriverQualificationsType.CcnlL116,
    DriverQualificationsType.CcnlL119,
]

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

const allowedCurrenciesInner = [
    'EUR',
]

const allowedCurrencies = [
    'EUR',
    'PLN',
]

const translateKey = 'italyConfiguration'

const country = 'italy'

export const ItalyMinimumWageSettingsPage = ({ match }: MinimalWagePageProps) => {
    const { companyId } = match.params
    const q = useMinimumWageQuery(companyId)
    const up = useMinimumWageMutation(companyId)
    const { t } = useLocalize()

    const [state, setState] = React.useState<ItalyMinimumWageConfigurationModel | null>(null)

    React.useEffect(() => {
        setState(null)
    }, [companyId])

    React.useEffect(() => {
        if (q.data) {
            const newState = structuredClone(q.data.italyConfiguration)
            setState(newState)
        }
    }, [q.data])

    const diffRenderers = React.useMemo(() => buildMinimumWageDiffRenderers('italyConfiguration', t), [t])
    const diffLabels = React.useMemo<Record<string, string>>(() => ({
        configuration: t('configuration.minimumWages.options'),
        expandableItems: t('configuration.minimumWages.minimalHourRateTitle'),
        allowanceExpandableItems: t('configuration.minimumWages.minimalAllowanceTitle'),
    }), [t])

    if (!state || !q.data) {
        return <PreLoader />
    }

    const isDirty = JSON.stringify(state) !== JSON.stringify(q.data.italyConfiguration)

    return (
        <MinimumWageCountryLayout
            selectedCountryValue={country}
            isDirty={isDirty}
            handleSave={() => up.mutateAsync({ ...q.data, italyConfiguration: state })}
            handleCancel={() => setState(q.data.italyConfiguration)}
            dataTestId="italy-minimum-wage-settings-page"
            isLoading={(q.isFetching || up.isPending)}
            diffBefore={q.data.italyConfiguration as Record<string, unknown>}
            diffAfter={state as Record<string, unknown>}
            diffLabels={diffLabels}
            diffRenderers={diffRenderers}
        >
            <CalculationStartDateSection
                state={state}
                setState={setState}
            />

            <div className={!state.configuration.calculateAmountsForCountry ? 'tw-opacity-50 tw-pointer-events-none' : ''}>
                    <GenericMinimumWageExpandableItemSection
                        allowedCurrencies={allowedCurrenciesInner}
                        qualificationTypes={QUALIFICATION_TYPES_ITALY} 
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageAllowanceExpandableItemSection
                        allowedCurrencies={allowedCurrencies}
                        state={state}
                        setState={setState}
                    />

                    <MinimumWageOptionsSection
                        className='tw-my-5'
                        loading={false}
                        fields={defaultFields}
                        translateKey={translateKey}
                        state={state}
                        accommodationAndAllowanceMaxDate={q.data.generalConfiguration.accommodationAndAllowanceMaxDate}
                        setState={setState}
                    />

                    <MinimumWagePolishOptionsSection 
                        className='tw-my-5'
                        loading={false}
                        state={state}
                        setState={setState}
                    />
            </div>

            <div style={{ minHeight: '90px' }} />
        </MinimumWageCountryLayout>
    )
}
